import { useState } from 'react';
import { Link } from 'react-router-dom';
import { getPurchaseProvider } from '../../services/purchase/purchaseProvider.js';
import { isIOS } from '../../services/platform.js';

/**
 * Controles que a App Store EXIGE numa tela que vende assinatura
 * (Guideline 3.1.1 e 3.1.2). Renderizado apenas no build nativo.
 *
 *  - "Restaurar compras": obrigatório. Sem isso, quem troca de aparelho ou
 *    reinstala perde acesso que já pagou — motivo de rejeição garantido.
 *  - "Gerenciar assinatura": substitui o customer portal do Stripe. A Apple
 *    não permite cancelar assinatura da loja por dentro do app; o caminho é
 *    abrir as configurações de assinatura do sistema.
 *  - Termos da assinatura (duração, renovação automática, links de Termos e
 *    Privacidade): a revisão verifica se estão visíveis na tela de compra.
 */
export function NativeSubscriptionControls({ appUserId, onRestored }) {
    const [restoring, setRestoring] = useState(false);
    const [message, setMessage] = useState(null);

    const handleRestore = async () => {
        setRestoring(true);
        setMessage(null);
        try {
            const provider = await getPurchaseProvider();
            const result = await provider.restorePurchases({ appUserId });
            if (result?.restored) {
                setMessage({ type: 'ok', text: 'Compras restauradas com sucesso.' });
                onRestored?.(result);
            } else {
                setMessage({ type: 'info', text: 'Nenhuma compra anterior encontrada nesta conta da loja.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err?.message || 'Não foi possível restaurar as compras.' });
        } finally {
            setRestoring(false);
        }
    };

    const handleManage = async () => {
        // itms-apps:// abre direto o painel de assinaturas do iOS; no Android
        // a URL equivalente da Play Store. Precisa sair do app, então é
        // App.openUrl e não o Browser in-app.
        const url = isIOS()
            ? 'itms-apps://apps.apple.com/account/subscriptions'
            : 'https://play.google.com/store/account/subscriptions';
        try {
            const { App } = await import('@capacitor/app');
            await App.openUrl({ url });
        } catch {
            setMessage({ type: 'error', text: 'Não foi possível abrir as assinaturas da loja.' });
        }
    };

    return (
        <div className="w-full max-w-2xl mx-auto mt-10 flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-3">
                <button
                    onClick={handleRestore}
                    disabled={restoring}
                    className="flex-1 min-h-[44px] font-bold py-3 px-4 rounded-xl text-xs bg-surface-container-high hover:bg-surface-container text-on-surface border border-outline-variant transition-colors disabled:opacity-50"
                >
                    {restoring ? 'Restaurando...' : 'Restaurar compras'}
                </button>
                <button
                    onClick={handleManage}
                    className="flex-1 min-h-[44px] font-bold py-3 px-4 rounded-xl text-xs bg-surface-container-high hover:bg-surface-container text-on-surface border border-outline-variant transition-colors"
                >
                    Gerenciar assinatura
                </button>
            </div>

            {message && (
                <p
                    role="status"
                    className={`text-sm text-center ${
                        message.type === 'error' ? 'text-error' : 'text-on-surface-variant'
                    }`}
                >
                    {message.text}
                </p>
            )}

            <div className="text-[11px] leading-relaxed text-on-surface-variant/70 text-center space-y-2">
                <p>
                    A assinatura é renovada automaticamente ao fim de cada período (trimestral ou
                    anual) pelo valor exibido acima, cobrado na sua conta da loja. A renovação pode
                    ser desativada a qualquer momento em “Gerenciar assinatura”, até 24 horas antes
                    do fim do período vigente.
                </p>
                <p className="flex items-center justify-center gap-3">
                    <Link to="/termos" className="underline hover:text-primary">Termos de Uso</Link>
                    <span aria-hidden="true">·</span>
                    <Link to="/privacidade" className="underline hover:text-primary">Política de Privacidade</Link>
                </p>
            </div>
        </div>
    );
}
